import { useEffect } from 'react'
import { Button, Form, Input, Modal, Select, Space } from 'antd'
import type { PlatformCreatePayload, PlatformType } from '@/features/platform/types'
import {
  clearCredentialFields,
  requiresApiCredentials,
  requiresKarbonCredentials,
  validatePlatformCredentials,
} from '@/features/platform/utils'
import {
  ADVICEPREP_TRIAL_TOAST_MS,
  advicePrepTrialLimitMessage,
  advicePrepTrialLimitRejection,
} from '@/features/adviceprep/trialLimit'

type Props = {
  open: boolean
  activeClientId: string
  createTypeOptions: { value: PlatformType; label: string }[]
  isPending: boolean
  onClose: () => void
  onCreate: (values: PlatformCreatePayload) => Promise<void>
  addToast: (message: string, type: 'success' | 'error' | 'warning' | 'info', duration?: number) => void
}

export function CreatePlatformModal({
  open,
  activeClientId,
  createTypeOptions,
  isPending,
  onClose,
  onCreate,
  addToast,
}: Props) {
  const [form] = Form.useForm<PlatformCreatePayload>()

  useEffect(() => {
    if (!open) return
    form.resetFields()
    form.setFieldsValue({ type: createTypeOptions[0]?.value })
  }, [createTypeOptions, form, open])

  const createType = Form.useWatch('type', form) as PlatformType | undefined
  const showCreateApiFields = !!createType && requiresApiCredentials(createType)
  const showCreateKarbonFields = !!createType && requiresKarbonCredentials(createType)

  const onCreateTypeChange = (nextType: PlatformType) => {
    form.setFieldsValue(clearCredentialFields(nextType))
  }

  const close = () => {
    form.resetFields()
    onClose()
  }

  const confirmCreate = async () => {
    if (!activeClientId) {
      addToast('Client ID is required', 'error')
      return
    }
    try {
      const values = await form.validateFields()
      const credentialError = validatePlatformCredentials(values)
      if (credentialError) {
        addToast(credentialError, 'warning')
        return
      }
      await onCreate(values)
      addToast('Platform created', 'success')
      close()
    } catch (e) {
      // A spent trial allowance is a policy answer, not a failure, and retrying
      // cannot succeed — so it closes and leans on the page's own explanation
      // rather than leaving the form open over a generic error.
      const trialLimit = advicePrepTrialLimitRejection(e)
      if (trialLimit) {
        // Longer than the 5s default: this names a date and runs to four lines,
        // and the modal vanishing under it costs the reader the first second.
        addToast(advicePrepTrialLimitMessage(trialLimit), 'warning', ADVICEPREP_TRIAL_TOAST_MS)
        close()
        return
      }

      const message = e instanceof Error ? e.message : 'Unable to create platform'
      addToast(message, 'error')
    }
  }

  return (
    <Modal
      open={open}
      title="Add new platform"
      onCancel={close}
      footer={
        <Space>
          <Button type="primary" loading={isPending} onClick={() => void confirmCreate()}>
            Create
          </Button>
          <Button onClick={close}>Cancel</Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="type" label="Platform Type" rules={[{ required: true, message: 'Platform type is required' }]}>
          <Select options={createTypeOptions} onChange={onCreateTypeChange} />
        </Form.Item>

        {showCreateApiFields && (
          <>
            <Form.Item name="apiKey" label="API Key" rules={[{ required: true, message: 'API Key is required' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="apiSecret" label="API Secret" rules={[{ required: true, message: 'API Secret is required' }]}>
              <Input />
            </Form.Item>
          </>
        )}

        {showCreateKarbonFields && (
          <>
            <Form.Item name="username" label="Username" rules={[{ required: true, message: 'Username is required' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Password is required' }]}>
              <Input.Password />
            </Form.Item>
            <Form.Item name="account" label="Account Name" rules={[{ required: true, message: 'Account is required' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="warehouse" label="Warehouse" rules={[{ required: true, message: 'Warehouse is required' }]}>
              <Input />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  )
}
